import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Plus, Trash2, ChevronDown, ChevronRight, ListTodo } from 'lucide-react';

// Default preparation tasks for podcast episodes
const DEFAULT_TASKS = [
    { id: 'default-1', text: 'Review guest bio', completed: false },
    { id: 'default-2', text: 'Prepare introduction', completed: false },
    { id: 'default-3', text: 'Test audio equipment', completed: false },
    { id: 'default-4', text: 'Prepare talking points', completed: false },
    { id: 'default-5', text: 'Research guest background', completed: false },
];

const PrepChecklist = ({ episodeId }) => {
    const [tasks, setTasks] = useState([]);
    const [newTaskText, setNewTaskText] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);
    const [isAddingTask, setIsAddingTask] = useState(false);

    // Storage key based on episode ID for persistence
    const storageKey = `prep-checklist-${episodeId || 'default'}`;

    // Load tasks from localStorage on mount
    useEffect(() => {
        try {
            const savedTasks = localStorage.getItem(storageKey);
            if (savedTasks) {
                const parsed = JSON.parse(savedTasks);
                setTasks(parsed);
            } else {
                // Initialize with default tasks if no saved tasks
                setTasks(DEFAULT_TASKS);
                localStorage.setItem(storageKey, JSON.stringify(DEFAULT_TASKS));
            }
        } catch (error) {
            console.warn('Failed to load tasks from localStorage:', error);
            setTasks(DEFAULT_TASKS);
        }
    }, [storageKey]);

    // Save tasks to localStorage whenever they change
    useEffect(() => {
        if (tasks.length > 0) {
            try {
                localStorage.setItem(storageKey, JSON.stringify(tasks));
            } catch (error) {
                console.warn('Failed to save tasks to localStorage:', error);
            }
        }
    }, [tasks, storageKey]);

    // Add a new task
    const addTask = () => {
        if (newTaskText.trim()) {
            const newTask = {
                id: `task-${Date.now()}`,
                text: newTaskText.trim(),
                completed: false,
            };
            setTasks(prev => [...prev, newTask]);
            setNewTaskText('');
            setIsAddingTask(false);
        }
    };

    // Toggle task completion
    const toggleTask = (taskId) => {
        setTasks(prev =>
            prev.map(task =>
                task.id === taskId
                    ? { ...task, completed: !task.completed }
                    : task
            )
        );
    };

    // Delete a task
    const deleteTask = (taskId) => {
        setTasks(prev => prev.filter(task => task.id !== taskId));
    };

    // Handle key press for adding task
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            addTask();
        } else if (e.key === 'Escape') {
            setIsAddingTask(false);
            setNewTaskText('');
        }
    };

    // Sort tasks: pending first, completed at bottom
    const sortedTasks = [...tasks].sort((a, b) => {
        if (a.completed === b.completed) return 0;
        return a.completed ? 1 : -1;
    });

    const completedCount = tasks.filter(t => t.completed).length;
    const totalCount = tasks.length;
    const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {/* Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <ListTodo size={16} className="text-indigo-500" />
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Prep Checklist
                    </h3>
                    {totalCount > 0 && (
                        <span className="text-[10px] font-medium text-slate-400 ml-1">
                            ({completedCount}/{totalCount})
                        </span>
                    )}
                </div>
                <motion.div
                    animate={{ rotate: isExpanded ? 0 : -90 }}
                    transition={{ duration: 0.2 }}
                >
                    <ChevronDown size={16} className="text-slate-400" />
                </motion.div>
            </button>

            {/* Progress Bar */}
            {totalCount > 0 && (
                <div className="px-4 pb-2">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercentage}%` }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                    </div>
                </div>
            )}

            {/* Task List */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 space-y-2">
                            {/* Tasks */}
                            <AnimatePresence mode="popLayout">
                                {sortedTasks.map((task) => (
                                    <motion.div
                                        key={task.id}
                                        layout
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 10 }}
                                        transition={{ duration: 0.15 }}
                                        className={`group flex items-center gap-2 p-2 rounded-xl transition-colors ${task.completed
                                                ? 'bg-slate-50/50'
                                                : 'bg-white hover:bg-slate-50'
                                            }`}
                                    >
                                        {/* Checkbox */}
                                        <button
                                            onClick={() => toggleTask(task.id)}
                                            className="flex-shrink-0 transition-transform hover:scale-110"
                                        >
                                            <AnimatePresence mode="wait">
                                                {task.completed ? (
                                                    <motion.div
                                                        key="completed"
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        exit={{ scale: 0 }}
                                                        transition={{ duration: 0.15 }}
                                                    >
                                                        <CheckCircle2
                                                            size={18}
                                                            className="text-green-500"
                                                        />
                                                    </motion.div>
                                                ) : (
                                                    <motion.div
                                                        key="pending"
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        exit={{ scale: 0 }}
                                                        transition={{ duration: 0.15 }}
                                                    >
                                                        <Circle
                                                            size={18}
                                                            className="text-slate-300 hover:text-indigo-400"
                                                        />
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </button>

                                        {/* Task Text */}
                                        <span
                                            className={`flex-1 text-xs leading-relaxed transition-colors ${task.completed
                                                    ? 'text-slate-400 line-through'
                                                    : 'text-slate-700'
                                                }`}
                                        >
                                            {task.text}
                                        </span>

                                        {/* Delete Button */}
                                        <button
                                            onClick={() => deleteTask(task.id)}
                                            className="flex-shrink-0 p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {/* Add Task Input */}
                            {isAddingTask ? (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-2 p-2 bg-indigo-50/50 rounded-xl"
                                >
                                    <Circle size={18} className="text-indigo-300" />
                                    <input
                                        type="text"
                                        value={newTaskText}
                                        onChange={(e) => setNewTaskText(e.target.value)}
                                        onKeyDown={handleKeyPress}
                                        onBlur={() => {
                                            if (!newTaskText.trim()) {
                                                setIsAddingTask(false);
                                            }
                                        }}
                                        placeholder="Add a task..."
                                        autoFocus
                                        className="flex-1 bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none"
                                    />
                                    <button
                                        onClick={addTask}
                                        disabled={!newTaskText.trim()}
                                        className="p-1 rounded-md bg-indigo-500 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-600 transition-colors"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.button
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    onClick={() => setIsAddingTask(true)}
                                    className="w-full flex items-center justify-center gap-1 p-2 text-xs text-slate-400 hover:text-indigo-500 hover:bg-indigo-50/50 rounded-xl transition-colors"
                                >
                                    <Plus size={14} />
                                    <span>Add task</span>
                                </motion.button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PrepChecklist;
